// Microbe v2 — entry point. Wires the kit (audio + haptics) to the three v2 surfaces (the
// persistent Canvas world, the genome bottom-sheet, the always-on HUD) through a single GameCtx,
// and owns boot: v1→v2 migration, offline catch-up, visibility/last-seen stamping, and the sound
// toggle (the button element is minted by the HUD; its behaviour is wired here so a change fans
// out through onChange to every surface).
//
// Idle economy note: the world's update() calls applyProduction() itself while unpaused, so main
// does NOT run a second idle loop or tick it here — that would double-accrue. Offline/return-to-
// foreground catch-up (grantOffline) is separate: it settles the gap from the saved lastSeen.

import { createAudio, haptic } from '../src/index.js';
import {
  save,
  rates,
  credit,
  addMass,
  canBuy,
  buyTrait,
  lvl,
  grantOffline,
  stampSeen,
} from './state.js';
import { migrate } from './migrate.js';
import { mountWorld } from './world.js';
import { mountGenome } from './genome.js';
import { mountHud } from './hud.js';
import type { GameCtx } from './types.js';

// ---- v1→v2 migration FIRST, before any module reads state ----
migrate();

// ---- DOM (guard the load-bearing three; #toast + back link are optional niceties) ----
const canvas = document.getElementById('stage') as HTMLCanvasElement | null;
const hudEl = document.getElementById('hud');
const genomeEl = document.getElementById('genome');
const toastEl = document.getElementById('toast');
const backLink = document.querySelector('.back');

if (!canvas || !hudEl || !genomeEl) {
  throw new Error('Microbe: missing #stage / #hud / #genome — cannot boot.');
}

// back-to-arcade link is a plain anchor; just add a tap tick (guarded — it may be absent).
if (backLink) backLink.addEventListener('click', () => haptic(8));

// ---- audio (procedural blips; enabled state persisted under save key 'soundOn') ----
const audio = createAudio({
  enabled: save.get('soundOn'),
  sounds: {
    eat: [{ freq: 520, to: 720, dur: 0.06, type: 'triangle', vol: 0.08 }],
    vesicle: [
      { freq: 660, dur: 0.05, type: 'sine', vol: 0.08 },
      { freq: 990, start: 0.05, dur: 0.09, type: 'sine', vol: 0.08 },
    ],
    buy: [
      { freq: 440, dur: 0.05, type: 'square', vol: 0.1 },
      { freq: 660, start: 0.05, dur: 0.07, type: 'square', vol: 0.1 },
    ],
    hurt: [{ freq: 200, to: 90, dur: 0.18, type: 'sawtooth', vol: 0.13 }],
    encyst: [{ freq: 320, to: 120, dur: 0.28, type: 'sine', vol: 0.12 }],
    levelup: [
      { freq: 523, dur: 0.08, type: 'square', vol: 0.11 },
      { freq: 659, start: 0.08, dur: 0.08, type: 'square', vol: 0.11 },
      { freq: 784, start: 0.16, dur: 0.14, type: 'square', vol: 0.11 },
    ],
    tick: [{ freq: 900, dur: 0.03, type: 'sine', vol: 0.05 }],
  },
});

// ---- change fan-out: HUD + genome subscribe; economy writes below notify() so both repaint ----
const listeners = new Set<() => void>();
function notify(): void {
  for (const fn of listeners) fn();
}

// ---- soft-pause flag (raised while the genome sheet is open; the world skips its sim) ----
let paused = false;

// ---- the single shared surface (implements the full types.ts GameCtx) ----
const ctx: GameCtx = {
  save,
  audio,
  haptic,

  // reads
  state: () => save.value,
  lvl: (id) => lvl(id),
  rates: () => rates(),
  wallet: () => save.value.wallet,

  // economy writes (persisted + clamped in state.ts) — each fans out a repaint
  credit(delta) {
    credit(delta);
    notify();
  },
  addMass(dm) {
    addMass(dm);
    notify();
  },
  canBuy: (id) => canBuy(id),
  buyTrait(id) {
    if (buyTrait(id)) {
      notify();
      return true;
    }
    return false;
  },

  // world / UI plumbing
  startPause() {
    paused = true;
    document.body.classList.add('genome-open');
  },
  endPause() {
    paused = false;
    document.body.classList.remove('genome-open');
  },
  isPaused: () => paused,
  sfx: (name) => audio.play(name),
  onChange(fn) {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
};

// ---- toast (while-you-were-away / events) ----
let toastTimer = 0;
function showToast(msg: string, ms = 4000): void {
  if (!toastEl) return;
  toastEl.textContent = msg;
  toastEl.hidden = false;
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    if (toastEl) toastEl.hidden = true;
  }, ms);
}

// Settle the offline gap from lastSeen and surface it (only when it was a real dormancy). Called
// at boot and on return-to-foreground; grantOffline re-stamps lastSeen so a span can't double-claim.
function runCatchup(): void {
  const away = grantOffline();
  if (away.seconds > 60) {
    showToast(
      `~${Math.round(away.seconds)}s dormant: +${Math.floor(away.energy)} energy, +${Math.floor(
        away.biomass,
      )} biomass`,
    );
  }
}

// ---- mount the three surfaces (ctx exists, so their closures are safe) ----
const world = mountWorld(canvas, ctx);
world.start();

const genome = mountGenome(genomeEl, ctx);
const hud = mountHud(hudEl, ctx, () => genome.open());

// ---- sound toggle: the HUD mints #soundBtn; wire its behaviour here so notify() repaints it ----
const soundBtn = document.getElementById('soundBtn') as HTMLButtonElement | null;
if (soundBtn) {
  soundBtn.addEventListener('click', () => {
    const on = !save.get('soundOn');
    save.patch({ soundOn: on });
    audio.setEnabled(on);
    haptic(8);
    if (on) audio.play('tick');
    notify();
  });
}

// ---- visibility / lifecycle: stamp on hide, catch up on return (unless soft-paused) ----
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    stampSeen();
  } else if (!paused) {
    runCatchup();
    notify();
  }
});
window.addEventListener('pagehide', () => stampSeen());

// ---- boot economy: one catch-up pass, then a first paint of every surface ----
runCatchup();
hud.render();
genome.render();
notify();
